import {
  ErrorResponse,
  FindProductsV2Request,
  ProductListV2,
  ProductV2,
  ProductsApi,
} from '@citruscamps/citrus-client'
import { Dispatch } from 'react'
import {
  IFetchManyProps,
  IFetchProps,
  IInfiniteProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import { DefaultDetailsPagination } from '../../../../../constants/pagination'

const DefaultFetchProps: ProductsFetchProps = {
  sort: 'created',
  order: 'DESC',
  query: {},
}

export type ProductsFilterProps = Omit<FindProductsV2Request, NonQueryAttributes>

export type ProductsFetchProps = IFetchProps<ProductsFilterProps>

type IProps = Pick<
  IFetchManyProps<ProductV2, ProductsFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'isEnabled' | 'setFetchProps'
>

interface FetchProducts {
  data: ProductV2[]
  error?: ErrorResponse
  fetchProps: ProductsFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setFetchProps: Dispatch<ProductsFetchProps>
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchProductsV2 = (props: IProps): FetchProducts => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<ProductV2, ProductsFilterProps>({
    type: 'product',
    programId: props.programId,
    fetchProps: props.fetchProps,
    defaultFetchProps: DefaultFetchProps,
    defaultPagination: props.pagination || DefaultDetailsPagination,
    isEnabled: props.isEnabled,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<ProductListV2> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new ProductsApi(generateApiConfig())
      return await client.findProductsV2(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          id: query.id,
          exclude_id: query.exclude_id,
          category_id:
            !!query?.category_id && query.category_id?.length > 0 ? query.category_id : undefined,
          parent_id: query?.parent_id || undefined,
          on_sale: query?.on_sale || undefined,
          is_visible: query?.is_visible || undefined,
        },
        { signal },
      )
    },
  })

  return {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setFetchProps,
    setPagination,
    setSearch,
  }
}

type IInfiniteProductsProps = Pick<
  IInfiniteProps<ProductV2, ProductsFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'fetchAll' | 'isEnabled'
>

interface FetchInfiniteProducts extends Omit<FetchProducts, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<ProductListV2, ErrorResponse>>
}

export const useFetchInfiniteProductsV2 = (
  props: IInfiniteProductsProps,
): FetchInfiniteProducts => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<ProductV2, ProductsFilterProps>({
    type: 'product',
    programId: props.programId,
    fetchProps: props.fetchProps || DefaultFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    fetchAll: props.fetchAll,
    isEnabled: props.isEnabled,
    queryFn: async (queryParams): Promise<ProductListV2> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new ProductsApi(generateApiConfig())
      return await client.findProductsV2(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          id: query.id,
          exclude_id: query.exclude_id,
          category_id:
            !!query?.category_id && query.category_id?.length > 0 ? query.category_id : undefined,
          parent_id: query?.parent_id || undefined,
          on_sale: query?.on_sale || undefined,
          is_visible: query?.is_visible || undefined,
        },
        { signal },
      )
    },
  })

  return {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  }
}
